import { createContext } from 'react';

// Editor preview device. Lives outside the Craft <Editor> so the canvas width
// switcher and the nodes (which dim/badge blocks hidden on the current device)
// share one value. Public render ignores this — it uses real CSS media queries.
export type Device = 'desktop' | 'tablet' | 'mobile';
export const DeviceCtx = createContext<Device>('desktop');
export const DEVICE_WIDTH: Record<Device, number | string> = { desktop: 1080, tablet: 768, mobile: 390 };

// Named device presets so designers can preview real handset/tablet widths (not just
// three generic buckets). `device` maps each to a bucket that drives per-device hide
// + the responsive-override preview; `width` sets the canvas.
export type DevicePreset = { label: string; width: number; device: Device };
export const DEVICE_PRESETS: DevicePreset[] = [
  { label: 'Galaxy S23', width: 360, device: 'mobile' },
  { label: 'iPhone SE', width: 375, device: 'mobile' },
  { label: 'iPhone 15', width: 393, device: 'mobile' },
  { label: 'Pixel 8', width: 412, device: 'mobile' },
  { label: 'iPhone 15 Pro Max', width: 430, device: 'mobile' },
  { label: 'iPad mini', width: 768, device: 'tablet' },
  { label: 'iPad Air', width: 820, device: 'tablet' },
  { label: 'iPad Pro 11″', width: 834, device: 'tablet' },
  { label: 'iPad Pro 12.9″', width: 1024, device: 'tablet' },
  { label: 'Laptop', width: 1080, device: 'desktop' },
  { label: 'Desktop', width: 1280, device: 'desktop' },
  { label: 'Wide', width: 1440, device: 'desktop' },
];
export const DEVICE_BUCKETS: Record<Device, string> = { mobile: 'Mobile', tablet: 'Tablet', desktop: 'Desktop' };

// Frame presets — "one canvas, many artifacts". 'web' is the responsive page (uses
// the device switcher); the others are fixed-size canvases for the assets the team
// wants to design in Triveny (email, social, slides, stories, print). Fixed frames
// set an explicit width AND height so the canvas matches the export dimensions.
export type FrameKind = 'web' | 'email' | 'social' | 'slide' | 'story' | 'a4';
export const FRAMES: Record<FrameKind, { label: string; width: number; height?: number }> = {
  web: { label: 'Web page', width: 1080 },
  email: { label: 'Email (600)', width: 600 },
  social: { label: 'Social 1:1', width: 1080, height: 1080 },
  slide: { label: 'Slide 16:9', width: 1280, height: 720 },
  story: { label: 'Story 9:16', width: 1080, height: 1920 },
  a4: { label: 'Print A4', width: 794, height: 1123 },
};
